import { Component, effect, input, ChangeDetectionStrategy } from "@angular/core";
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from "@angular/forms";
import { DropdownMultipleComponent } from "rl-common/components/dropdown/dropdown-multiple/dropdown-multiple.component";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableMessageDirective } from "rl-common/components/form-table/form-table-message.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { TextAreaComponent } from "rl-common/components/text/text-area/text-area.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { IOneConfigWorkflowAction } from "rl-common/services/one-config/one-config.models";
import { OneConfigService } from "rl-common/services/one-config/one-config.service";
import { SessionService } from "rl-common/services/session.service";
import { TaskConfig } from "rl-common/services/task-config/task-config.models";

export interface ITaskConfigurationForm {
	taskName: FormControl<string>;
	description: FormControl<string>;
	initAction: FormControl<IOneConfigWorkflowAction>;
	blockActions: FormControl<IOneConfigWorkflowAction[]>;
}

@Component({
	selector: "rl-workflow-task-form",
	imports: [FormTableRowComponent, FormTableControlDirective, FormTableMessageDirective, TextInputComponent, TextAreaComponent, ReactiveFormsModule, DropdownSingleComponent, DropdownMultipleComponent],
	templateUrl: "./workflow-task-form.component.html",
	changeDetection: ChangeDetectionStrategy.Eager,
	styleUrl: "./workflow-task-form.component.scss",
})
export class WorkflowTaskFormComponent {

	charTypeId = input.required<number>();
	processId = input.required<number>();
	task = input<TaskConfig>(null);

	form: FormGroup<ITaskConfigurationForm>;

	initialActionsOptions: DropdownOptions<IOneConfigWorkflowAction> = {
		items: [],
		rowKeyFn: (row: IOneConfigWorkflowAction) => row.actionId,
		rowLabelFn: (row: IOneConfigWorkflowAction) => row.actionName
	};

	blockActionsOptions: DropdownOptions<IOneConfigWorkflowAction> = {
		items: [],
		rowKeyFn: (row: IOneConfigWorkflowAction) => row.actionId,
		rowLabelFn: (row: IOneConfigWorkflowAction) => row.actionName
	};

	get isValid() {
		return this.form?.valid;
	}

	get isDirty() {
		return this.form?.dirty;
	}

	get mappedTask(): TaskConfig {
		if (!this.form?.controls) {
			return null;
		}
		const blockingActions = this.form.controls.blockActions.value ?? [];
		const task: TaskConfig = {
			divId: this._session.divId,
			processId: this.processId(),
			id: this.task()?.id,
			taskName: this.form.controls.taskName.value,
			taskDescription: this.form.controls.description.value,
			actionId: this.form.controls.initAction.value?.actionId,
			actionsBlocked: blockingActions.map(x => x.actionId).join(",")
		};
		return task;
	}

	constructor(
		private readonly _formBuilder: FormBuilder,
		private readonly _oneConfig: OneConfigService,
		private readonly _session: SessionService
	) {
		effect(() => {
			const task = this.task();
			this.buildForm(task);
		});
	}

	buildForm(task: TaskConfig): void {
		const workflowProcess = this._oneConfig.getWorkflowProcess(this.charTypeId(), this.processId());
		if (!workflowProcess) {
			return;
		}

		// set up the initial action form control
		this.initialActionsOptions.items = [...workflowProcess.workflowActions];
		const initialAction = this.initialActionsOptions.items.find(x => x.actionId === task?.actionId);

		// set up the block actions form control
		this.blockActionsOptions.items = [...workflowProcess.workflowActions];
		const blockActions = (task?.actionsBlocked ?? "").split(",").map(x => +x).filter(x => x > 0);
		const selectedBlockActions = this.blockActionsOptions.items.filter(x => blockActions.includes(x.actionId));

		this.form = this._formBuilder.group<ITaskConfigurationForm>({
			taskName: this._formBuilder.control(task?.taskName, { validators: [Validators.required] }),
			description: this._formBuilder.control(task?.taskDescription),
			initAction: this._formBuilder.control(initialAction, { validators: [Validators.required] }), // TODO
			blockActions: this._formBuilder.control(selectedBlockActions)
		});
	}

}
